import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMessage, getAllDisputes, updateDisputeStatus } from '../../../features/disputeSlice';
import AdminPageShell from "../components/AdminPageShell";
import {
  AlertCircle,
  MessageSquare,
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MessageCircle,
  Eye,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  FileText,
  User,
  Calendar,
  DollarSign,
  Shield,
  Activity,
  Settings,
  Send,
  Reply,
  Flag,
  Star,
  Phone,
  Mail,
  MapPin,
  Package,
  CreditCard,
  Truck,
  Star as StarIcon,
} from 'lucide-react';

const ACCENT = "#D2042D";
const cx = (...arr) => arr.filter(Boolean).join(" ");

const tone = {
  good: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
    dot: "bg-emerald-500",
  },
  warn: {
    badge: "bg-amber-50 text-amber-700 border-amber-100",
    dot: "bg-amber-500",
  },
  bad: {
    badge: "bg-rose-50 text-rose-700 border-rose-100",
    dot: "bg-rose-500",
  },
  neutral: {
    badge: "bg-slate-50 text-slate-700 border-slate-100",
    dot: "bg-slate-400",
  },
};

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full" style={{ background: ACCENT }} />
        <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-xs text-slate-500 line-clamp-2">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const Field = ({ className, ...props }) => (
  <input
    {...props}
    className={cx(
      "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30",
      className
    )}
  />
);

const Select = ({ className, ...props }) => (
  <select
    {...props}
    className={cx(
      "rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30",
      className
    )}
  />
);

const DisputeManagement = () => {
  const dispatch = useDispatch();
  const { disputes, loading, error } = useSelector((state) => state.dispute);
  
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    search: ''
  });
  const [resolutionForm, setResolutionForm] = useState({
    action: '',
    amount: '',
    currency: 'USD',
    notes: ''
  });
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState('');
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    dispatch(getAllDisputes());
  }, [dispatch]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleStatusUpdate = async (disputeId, status) => {
    try {
      await dispatch(updateDisputeStatus({ disputeId, status })).unwrap();
      // Refresh disputes
      dispatch(getAllDisputes());
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleResolutionSubmit = async () => {
    if (!selectedDispute || !resolutionForm.action) return;
    
    try {
      const resolution = {
        action: resolutionForm.action,
        amount: resolutionForm.amount ? parseFloat(resolutionForm.amount) : undefined,
        currency: resolutionForm.currency,
        notes: resolutionForm.notes
      };
      
      await dispatch(updateDisputeStatus({ 
        disputeId: selectedDispute._id, 
        status: 'resolved',
        resolution 
      })).unwrap();
      
      setResolutionForm({
        action: '',
        amount: '',
        currency: 'USD',
        notes: ''
      });
      
      // Refresh disputes
      dispatch(getAllDisputes());
    } catch (error) {
      console.error('Failed to submit resolution:', error);
    }
  };

  const handleSendReply = async () => {
    if (!selectedDispute?._id || !replyText.trim()) return;
    setReplyError('');
    try {
      const res = await dispatch(addMessage({ disputeId: selectedDispute._id, message: replyText.trim() })).unwrap();
      setSelectedDispute(res?.data || selectedDispute);
      setReplyText('');
      dispatch(getAllDisputes());
    } catch (err) {
      setReplyError(err?.message || 'Failed to send reply');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="w-4 h-4 text-slate-600" />;
      case 'under_review':
        return <AlertCircle className="w-4 h-4 text-amber-700" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-emerald-700" />;
      case 'closed':
        return <XCircle className="w-4 h-4 text-slate-500" />;
      case 'escalated':
        return <AlertTriangle className="w-4 h-4 text-rose-700" />;
      default:
        return <Clock className="w-4 h-4 text-slate-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      order_issue: 'Order Issue',
      delivery_problem: 'Delivery Problem',
      product_quality: 'Product Quality',
      payment_issue: 'Payment Issue',
      refund_request: 'Refund Request',
      general_complaint: 'General Complaint',
      appeal: 'Appeal'
    };
    return categories[category] || category;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "border-slate-200 bg-slate-50 text-slate-700",
      medium: "border-slate-200 bg-white text-slate-700",
      high: "border-amber-200 bg-amber-50 text-amber-800",
      urgent: "border-rose-200 bg-rose-50 text-rose-800",
    };
    return colors[priority] || colors.medium;
  };

  const filteredDisputes = disputes?.filter(dispute => {
    if (filters.status && dispute.status !== filters.status) return false;
    if (filters.category && dispute.category !== filters.category) return false;
    if (filters.priority && dispute.priority !== filters.priority) return false;
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return (
        dispute.subject.toLowerCase().includes(searchTerm) ||
        dispute.description.toLowerCase().includes(searchTerm) ||
        dispute.user?.name?.toLowerCase().includes(searchTerm) ||
        dispute.order?.orderId?.toLowerCase().includes(searchTerm)
      );
    }
    return true;
  });

  // Analytics data
  const analytics = {
    total: disputes?.length || 0,
    open: disputes?.filter(d => d.status === 'open').length || 0,
    underReview: disputes?.filter(d => d.status === 'under_review').length || 0,
    resolved: disputes?.filter(d => d.status === 'resolved').length || 0,
    escalated: disputes?.filter(d => d.status === 'escalated').length || 0,
    urgent: disputes?.filter(d => d.priority === 'urgent').length || 0,
    avgResolutionTime: '2.5 days',
    satisfactionRate: '94.2%'
  };

  const MetricCard = ({ title, value, change, trend, icon: Icon }) => (
    <Card className="p-4 md:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            {title}
          </p>
          <p className="mt-2 text-2xl md:text-[28px] font-black tracking-tight text-slate-900">
            {value}
          </p>
          {change ? (
            <div className="mt-2 flex items-center gap-2">
              <span
                className={cx(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold",
                  trend === "up" ? tone.good.badge : tone.bad.badge
                )}
              >
                {trend === "up" ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {change}
              </span>
            </div>
          ) : null}
        </div>

        <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
          <Icon size={18} className="text-slate-700" />
        </div>
      </div>
    </Card>
  );

  const DisputeCard = ({ dispute }) => {
    const status = dispute.status;
    const priority = dispute.priority;

    const statusTone =
      status === "resolved"
        ? "good"
        : status === "under_review"
          ? "warn"
          : status === "escalated"
            ? "bad"
            : "neutral";

    return (
      <Card className="p-5 hover:bg-slate-50/60 transition-colors">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
              {getStatusIcon(status)}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{dispute.subject}</h3>
              <p className="text-sm text-slate-500 truncate">{dispute.user?.name || 'Unknown User'}</p>
              <div className="mt-2">
                <span
                  className={cx(
                    "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-bold",
                    tone[statusTone].badge
                  )}
                >
                  <span className={cx("h-2 w-2 rounded-full", tone[statusTone].dot)} />
                  {status.replace("_", " ")}
                </span>
              </div>
            </div>
          </div>
          <span
            className={cx(
              "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold capitalize",
              getPriorityColor(priority)
            )}
          >
            {priority}
          </span>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-sm text-slate-600">{dispute.description.substring(0, 100)}...</p>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div>
              <p className="text-slate-500">Category</p>
              <p className="font-medium text-slate-900">{getCategoryLabel(dispute.category)}</p>
            </div>
            <div>
              <p className="text-slate-500">Created</p>
              <p className="font-medium text-slate-900">{new Date(dispute.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-slate-500">Messages</p>
              <p className="font-medium text-slate-900">{dispute.messages?.length || 0}</p>
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button 
                  onClick={() => setSelectedDispute(dispute)}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors" 
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                  title="Reply"
                  onClick={() => setSelectedDispute(dispute)}
                >
                  <Reply className="w-4 h-4" />
                </button>
                <button className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors" title="Escalate">
                  <Flag className="w-4 h-4" />
                </button>
              </div>
              <button className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors" title="More">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <AdminPageShell
        title="Support Management"
        description="Manage and resolve customer support tickets efficiently."
      >
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-96 bg-slate-200 rounded"></div>
            <div className="lg:col-span-2 h-96 bg-slate-200 rounded"></div>
          </div>
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Support Management"
      description="Manage and resolve customer support tickets efficiently."
      actions={
        <>
          <button
            type="button"
            onClick={() => dispatch(getAllDisputes())}
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            New Ticket
          </button>
        </>
      }
    >

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Tickets"
            value={analytics.total}
            change="+5.2%"
            trend="up"
            icon={FileText}
            color="blue"
          />
          <MetricCard
            title="Open Cases"
            value={analytics.open}
            change="+12.3%"
            trend="up"
            icon={Clock}
            color="yellow"
          />
          <MetricCard
            title="Resolved"
            value={analytics.resolved}
            change="+8.7%"
            trend="up"
            icon={CheckCircle}
            color="green"
          />
          <MetricCard
            title="Urgent Cases"
            value={analytics.urgent}
            change="+2.1%"
            trend="down"
            icon={AlertTriangle}
            color="red"
          />
        </div>

        {/* Ticket Status Overview */}
        <Card className="p-5 mb-8">
          <SectionHead title="Ticket Status Overview" subtitle="At-a-glance case distribution." />
          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: "Open", value: analytics.open, icon: Clock, toneKey: "neutral" },
              { label: "Under Review", value: analytics.underReview, icon: AlertCircle, toneKey: "warn" },
              { label: "Resolved", value: analytics.resolved, icon: CheckCircle, toneKey: "good" },
              { label: "Escalated", value: analytics.escalated, icon: AlertTriangle, toneKey: "bad" },
              { label: "Avg Resolution", value: analytics.avgResolutionTime, icon: Activity, toneKey: "neutral" },
            ].map(({ label, value, icon: Icon, toneKey }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={cx("h-2 w-2 rounded-full", tone[toneKey].dot)} />
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">{label}</p>
                  </div>
                  <Icon className="w-5 h-5 text-slate-700" />
                </div>
                <p className="mt-3 text-xl font-black tracking-tight text-slate-900">{value}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Search and Filters */}
        <Card className="p-5 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Field
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search tickets..."
                  className="pl-11"
                />
              </div>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="under_review">Under Review</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="escalated">Escalated</option>
              </Select>
              <Select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
              >
                <option value="">All Categories</option>
                <option value="order_issue">Order Issue</option>
                <option value="delivery_problem">Delivery Problem</option>
                <option value="product_quality">Product Quality</option>
                <option value="payment_issue">Payment Issue</option>
                <option value="refund_request">Refund Request</option>
                <option value="general_complaint">General Complaint</option>
                <option value="appeal">Appeal</option>
              </Select>
              <Select
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Select>
            </div>
            <div className="flex gap-2">
              <SecondaryButton
                onClick={() => setFilters({ status: '', category: '', priority: '', search: '' })}
              >
                <Filter className="w-4 h-4" />
                <span>Clear Filters</span>
              </SecondaryButton>

              <div className="flex rounded-2xl border border-slate-200 bg-white p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={cx(
                    "h-9 px-3 rounded-xl text-sm font-semibold transition-colors",
                    viewMode === 'list'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  )}
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={cx(
                    "h-9 px-3 rounded-xl text-sm font-semibold transition-colors",
                    viewMode === 'grid'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  )}
                >
                  Grid
                </button>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <Card className="overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
                <h3 className="text-sm font-black tracking-tight text-slate-900">Tickets</h3>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                  <span className="font-semibold text-slate-900">{filteredDisputes?.length || 0}</span>
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {filteredDisputes?.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No tickets found</p>
                  </div>
                ) : (
                  filteredDisputes?.map((dispute) => {
                    const isActive = selectedDispute?._id === dispute._id;
                    const itemTone =
                      dispute.status === "resolved"
                        ? "good"
                        : dispute.status === "under_review"
                          ? "warn"
                          : dispute.status === "escalated"
                            ? "bad"
                            : "neutral";

                    return (
                      <div
                        key={dispute._id}
                        onClick={() => setSelectedDispute(dispute)}
                        className={cx(
                          "p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors",
                          isActive ? "bg-slate-50" : "bg-white"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-sm text-slate-900 truncate">{dispute.subject}</h4>
                          <span className={cx("h-2 w-2 rounded-full", tone[itemTone].dot)} />
                        </div>
                        <p className="text-xs text-slate-600 mb-2">
                          {dispute.user?.name || 'Unknown User'}
                        </p>
                        <div className="flex items-center justify-between">
                          <span
                            className={cx(
                              "text-xs px-2 py-1 rounded-full border font-bold capitalize",
                              getPriorityColor(dispute.priority)
                            )}
                          >
                            {dispute.priority}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(dispute.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </div>

          {/* Dispute Details */}
          <div className="lg:col-span-2">
            {selectedDispute ? (
              <Card className="overflow-hidden">
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Case</p>
                      <h3 className="text-xl font-black tracking-tight text-slate-900 truncate">{selectedDispute.subject}</h3>
                    </div>
                    <span className={cx(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold",
                      selectedDispute.status === "resolved"
                        ? tone.good.badge
                        : selectedDispute.status === "under_review"
                          ? tone.warn.badge
                          : selectedDispute.status === "escalated"
                            ? tone.bad.badge
                            : tone.neutral.badge
                    )}>
                      <span className={cx(
                        "h-2 w-2 rounded-full",
                        selectedDispute.status === "resolved"
                          ? tone.good.dot
                          : selectedDispute.status === "under_review"
                            ? tone.warn.dot
                            : selectedDispute.status === "escalated"
                              ? tone.bad.dot
                              : tone.neutral.dot
                      )} />
                      {selectedDispute.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
                    <div>
                      <span className="text-slate-500">User</span>
                      <p className="font-medium">{selectedDispute.user?.name || 'Unknown'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Category</span>
                      <p className="font-medium">{getCategoryLabel(selectedDispute.category)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Priority</span>
                      <p>
                        <span className={cx(
                          "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold capitalize",
                          getPriorityColor(selectedDispute.priority)
                        )}>
                          {selectedDispute.priority}
                        </span>
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500">Created</span>
                      <p className="font-medium">{new Date(selectedDispute.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-black tracking-tight text-slate-900">Description</h4>
                    <p className="mt-2 text-sm text-slate-700 bg-slate-50 p-4 rounded-2xl border border-slate-200">{selectedDispute.description}</p>
                  </div>

                  {/* Messages */}
                  <div className="mb-6">
                    <h4 className="text-sm font-black tracking-tight text-slate-900 flex items-center">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Messages ({selectedDispute.messages?.length || 0})
                    </h4>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {selectedDispute.messages?.map((message, index) => (
                        <div
                          key={index}
                          className={cx(
                            "p-4 rounded-2xl border border-slate-200",
                            message.sender === 'user'
                              ? 'bg-slate-50 border-l-4 border-l-slate-300'
                              : 'bg-white border-l-4 border-l-[#D2042D]'
                          )}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold capitalize text-slate-900">{message.sender}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(message.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">{message.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Reply */}
                  <div className="mb-6">
                    <h4 className="text-sm font-black tracking-tight text-slate-900">Reply to ticket</h4>
                    <div className="mt-3">
                      <textarea
                        rows={3}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write a response to the customer..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                      />
                      {replyError ? <p className="mt-2 text-xs text-rose-600">{replyError}</p> : null}
                      <div className="mt-3 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={handleSendReply}
                          disabled={!replyText.trim()}
                          className={cx(
                            "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                            replyText.trim()
                              ? "border-slate-900 bg-slate-900 text-white hover:bg-[#D2042D]"
                              : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                          )}
                        >
                          <Send className="w-4 h-4" />
                          Send reply
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div className="mb-6">
                    <h4 className="text-sm font-black tracking-tight text-slate-900">Update Status</h4>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {['open', 'under_review', 'resolved', 'closed'].map((status) => (
                        <button
                          key={status}
                          onClick={() => handleStatusUpdate(selectedDispute._id, status)}
                          disabled={selectedDispute.status === status}
                          className={cx(
                            "h-10 px-4 rounded-2xl text-sm font-semibold border transition-colors",
                            selectedDispute.status === status
                              ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                              : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                          )}
                        >
                          {status.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Resolution Form */}
                  {selectedDispute.status === 'under_review' && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                      <h4 className="text-sm font-black tracking-tight text-amber-900">Resolution</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-black uppercase tracking-[0.18em] text-amber-700 mb-1">Action</label>
                          <Select
                            value={resolutionForm.action}
                            onChange={(e) => setResolutionForm({ ...resolutionForm, action: e.target.value })}
                          >
                            <option value="">Select Action</option>
                            <option value="refund">Full Refund</option>
                            <option value="partial_refund">Partial Refund</option>
                            <option value="replacement">Replacement</option>
                            <option value="compensation">Compensation</option>
                            <option value="no_action">No Action</option>
                            <option value="other">Other</option>
                          </Select>
                        </div>
                        
                        {resolutionForm.action && resolutionForm.action !== 'no_action' && (
                          <div>
                            <label className="block text-xs font-black uppercase tracking-[0.18em] text-amber-700 mb-1">Amount</label>
                            <div className="flex space-x-2">
                              <Field
                                type="number"
                                value={resolutionForm.amount}
                                onChange={(e) => setResolutionForm({ ...resolutionForm, amount: e.target.value })}
                                placeholder="0.00"
                              />
                              <Select
                                value={resolutionForm.currency}
                                onChange={(e) => setResolutionForm({ ...resolutionForm, currency: e.target.value })}
                              >
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="BDT">BDT</option>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4">
                        <label className="block text-xs font-black uppercase tracking-[0.18em] text-amber-700 mb-1">Notes</label>
                        <textarea
                          value={resolutionForm.notes}
                          onChange={(e) => setResolutionForm({ ...resolutionForm, notes: e.target.value })}
                          rows="3"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                          placeholder="Additional notes about the resolution..."
                        />
                      </div>
                      
                      <div className="mt-4">
                        <PrimaryButton
                          onClick={handleResolutionSubmit}
                          disabled={!resolutionForm.action}
                          className={cx(
                            "disabled:bg-slate-200 disabled:text-slate-400 disabled:hover:bg-slate-200 disabled:cursor-not-allowed"
                          )}
                        >
                          Submit Resolution
                        </PrimaryButton>
                      </div>
                    </div>
                  )}

                  {/* Resolution Info */}
                  {selectedDispute.resolution && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                      <h4 className="text-sm font-black tracking-tight text-emerald-900">Resolution Applied</h4>
                      <div className="mt-2 text-sm text-emerald-800">
                        <p><strong>Action:</strong> {selectedDispute.resolution.action}</p>
                        {selectedDispute.resolution.amount && (
                          <p><strong>Amount:</strong> {selectedDispute.resolution.amount} {selectedDispute.resolution.currency}</p>
                        )}
                        {selectedDispute.resolution.notes && (
                          <p><strong>Notes:</strong> {selectedDispute.resolution.notes}</p>
                        )}
                        <p><strong>Resolved:</strong> {new Date(selectedDispute.resolution.resolvedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 text-slate-200" />
                <p>Select a dispute to view details</p>
              </Card>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm">
            {error}
          </div>
        )}
    </AdminPageShell>
  );
};

export default DisputeManagement; 